import { describeAccion } from "@/lib/describeAccion";

// Convierte un registro de auditoría (acción + datos guardados) en una explicación en lenguaje
// normal: una frase de "qué se hizo" y una lista legible de los datos. Nunca se enseña JSON.
export interface RegistroParaExplicar {
  accion: string;
  entidad: string | null;
  entidadId: number | null;
  detalle: Record<string, unknown> | null;
  usuario: { nombre: string; apellidos: string | null } | null;
  club: { nombre: string } | null;
}

export interface ExplicacionRegistro {
  titulo: string;
  resumen: string;
  datos: { etiqueta: string; valor: string }[];
}

const ETIQUETAS: Record<string, string> = {
  nombre: "Nombre",
  apellidos: "Apellidos",
  descripcion: "Descripción",
  estado: "Estado",
  email: "Email",
  role: "Rol",
  telefono: "Teléfono",
  direccion: "Dirección",
  localidad: "Localidad",
  provincia: "Provincia",
  comunidadAutonoma: "Comunidad autónoma",
  pais: "País",
  active: "Activo",
  activo: "Activo",
  tipo: "Tipo",
  motivo: "Motivo",
  pista: "Pista",
  startsAt: "Desde",
  endsAt: "Hasta",
  sourceMode: "Modo de origen",
  permisosCambiados: "Permisos modificados",
  campeonato: "Campeonato",
  equipo: "Pareja",
  usuario: "Usuario",
  fase: "Fase",
  clubes: "Clubes",
  nuevoProveedor: "Proveedor dado de alta",
  rtspUrl: "Dirección de la cámara",
};

const ROLES: Record<string, string> = { SUPER_ADMIN: "Super-admin", PROVIDER: "Proveedor", CLUB: "Usuario de club", REFEREE: "Árbitro" };
const ESTADOS: Record<string, string> = { BORRADOR: "Borrador", EN_CURSO: "En curso", FINALIZADO: "Finalizado", CANCELADO: "Cancelado" };
const TIPOS_DISPOSITIVO: Record<string, string> = {
  SCREEN_PREVIEW: "Pantalla de partidos en directo",
  SCREEN_SCOREBOARD: "Pantalla de marcador de pista",
  TABLET: "Tablet de árbitro",
};
const MODOS_CAMARA: Record<string, string> = { PULL: "El servidor la lee directamente", PUSH: "Un equipo remoto la envía" };
const ENTIDADES: Record<string, string> = { Camara: "Cámara" };

// Claves cuyo valor nunca se enseña (el servidor ya las enmascara, esto es una segunda red).
const CLAVES_SENSIBLES = /pass|token|hash|secret|rtsp|apikey|api_key|clave/i;

const fechaHora = new Intl.DateTimeFormat("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function humanizar(clave: string): string {
  const spaced = clave.replace(/([A-Z])/g, " $1").toLowerCase().trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function formatValor(clave: string, valor: unknown): string {
  if (valor === null || valor === undefined || valor === "") return "—";
  if (CLAVES_SENSIBLES.test(clave) || valor === "***") return "Dato oculto por seguridad";
  if (typeof valor === "boolean") return valor ? "Sí" : "No";
  if (Array.isArray(valor)) return valor.map((v) => formatValor(clave, v)).join(", ");
  if (typeof valor === "number") return String(valor);
  if (typeof valor === "string") {
    if (clave === "role") return ROLES[valor] ?? valor;
    if (clave === "estado") return ESTADOS[valor] ?? valor;
    if (clave === "tipo") return TIPOS_DISPOSITIVO[valor] ?? valor;
    if (clave === "sourceMode") return MODOS_CAMARA[valor] ?? valor;
    if (/^\d{4}-\d{2}-\d{2}T/.test(valor) && !Number.isNaN(Date.parse(valor))) return fechaHora.format(new Date(valor));
    return valor;
  }
  return JSON.stringify(valor);
}

export function etiquetaEntidad(entidad: string | null): string {
  return entidad ? (ENTIDADES[entidad] ?? entidad) : "—";
}

export function explicarRegistro(r: RegistroParaExplicar): ExplicacionRegistro {
  const d = r.detalle ?? {};
  const quien = r.usuario ? `${r.usuario.nombre} ${r.usuario.apellidos ?? ""}`.trim() : "El sistema";
  const str = (k: string): string => (typeof d[k] === "string" ? (d[k] as string) : "");
  const q = (v: string) => (v ? `«${v}»` : "");
  const enClub = r.club ? ` del club «${r.club.nombre}»` : "";
  const num = r.entidadId != null ? ` nº ${r.entidadId}` : "";

  // Datos legibles: sin ids internos (no dicen nada a una persona) ni valores sensibles.
  const claves = Object.keys(d).filter((k) => !/Id$/.test(k));
  const datos = claves.map((k) => ({ etiqueta: ETIQUETAS[k] ?? humanizar(k), valor: formatValor(k, d[k]) }));
  const cambios = datos.map((x) => x.etiqueta);
  const conCambios = (base: string) => (cambios.length ? `${base}. Datos cambiados: ${cambios.join(", ")}.` : `${base}.`);

  let resumen: string;
  switch (r.accion) {
    case "auth.login":
      resumen = `${quien} inició sesión en el panel.`;
      break;
    case "auth.restablecer_contrasena":
      resumen = `${quien} restableció su contraseña con el enlace de recuperación que recibió por email.`;
      break;

    case "usuario.crear":
      resumen = `${quien} creó el usuario ${str("email")} con el rol ${formatValor("role", d.role)}${enClub}.`;
      break;
    case "usuario.actualizar":
      resumen = conCambios(`${quien} modificó los datos de un usuario${enClub}`);
      break;
    case "usuario.desactivar":
      resumen = `${quien} desactivó al usuario ${str("email")}${enClub}. Ya no puede entrar al panel.`;
      break;

    case "club.crear":
      resumen = `${quien} creó el club ${q(str("nombre"))}${str("nuevoProveedor") ? ` y dio de alta a su proveedor (${str("nuevoProveedor")})` : ""}.`;
      break;
    case "club.actualizar":
      resumen = conCambios(`${quien} modificó los datos del club${r.club ? ` «${r.club.nombre}»` : ""}`);
      break;
    case "club.desactivar":
      resumen = `${quien} desactivó el club${r.club ? ` «${r.club.nombre}»` : num}.`;
      break;

    case "pista.crear":
      resumen = `${quien} creó la pista ${q(str("nombre"))}${enClub}.`;
      break;
    case "pista.actualizar":
      resumen = conCambios(`${quien} modificó los datos de una pista${enClub}`);
      break;
    case "pista.desactivar":
      resumen = `${quien} desactivó una pista${enClub}.`;
      break;
    case "pista.bloquear":
      resumen = `${quien} bloqueó la pista ${q(str("pista"))}${enClub} desde ${formatValor("startsAt", d.startsAt)} hasta ${formatValor("endsAt", d.endsAt)}${str("motivo") ? `. Motivo: ${str("motivo")}` : ""}.`;
      break;
    case "pista.desbloquear":
      resumen = `${quien} quitó el bloqueo de una pista${enClub}.`;
      break;

    case "camara.crear":
      resumen = `${quien} añadió la cámara ${q(str("nombre"))}${enClub}.`;
      break;
    case "camara.actualizar":
      resumen = conCambios(`${quien} modificó la configuración de una cámara${enClub}`);
      break;
    case "camara.eliminar":
      resumen = `${quien} eliminó la cámara ${q(str("nombre"))}${enClub}.`;
      break;

    case "dispositivo.crear":
      resumen = `${quien} dio de alta el dispositivo ${q(str("nombre"))}${enClub}.`;
      break;
    case "dispositivo.actualizar":
      resumen = conCambios(`${quien} modificó la configuración de un dispositivo${enClub}`);
      break;
    case "dispositivo.regenerar_token":
      resumen = `${quien} generó un nuevo enlace de acceso para un dispositivo${enClub}. El enlace anterior dejó de funcionar.`;
      break;
    case "dispositivo.eliminar":
      resumen = `${quien} eliminó el dispositivo ${q(str("nombre"))}${enClub}.`;
      break;

    case "jugador.actualizar":
      resumen = `${quien} modificó los datos de un jugador${enClub}.`;
      break;

    case "registro.aprobar":
      resumen = `${quien} aprobó la solicitud de registro del proveedor ${str("email")}${Array.isArray(d.clubes) && d.clubes.length ? `, con los clubes: ${(d.clubes as string[]).join(", ")}` : ""}.`;
      break;
    case "registro_arbitro.aprobar":
      resumen = `${quien} aprobó la solicitud de registro del árbitro ${str("email")}. Ya puede entrar con su cuenta.`;
      break;

    case "campeonato.crear":
      resumen = `${quien} creó el campeonato ${q(str("nombre"))}.`;
      break;
    case "campeonato.actualizar":
      resumen = conCambios(`${quien} modificó los datos del campeonato${num}`);
      break;
    case "campeonato.eliminar":
      resumen = `${quien} eliminó el campeonato ${q(str("nombre"))}, con todas sus parejas y partidos.`;
      break;
    case "campeonato_gestor.crear":
      resumen = `${quien} dio permiso para gestionar el campeonato ${q(str("campeonato"))} a ${str("usuario")}.`;
      break;
    case "campeonato_gestor.eliminar":
      resumen = `${quien} quitó a ${str("usuario")} el permiso para gestionar el campeonato ${q(str("campeonato"))}.`;
      break;
    case "campeonato_equipo.crear":
      resumen = `${quien} inscribió la pareja ${q(str("equipo"))} en el campeonato ${q(str("campeonato"))}.`;
      break;
    case "campeonato_equipo.actualizar":
      resumen = `${quien} modificó la pareja ${q(str("equipo"))} del campeonato ${q(str("campeonato"))}.`;
      break;
    case "campeonato_equipo.eliminar":
      resumen = `${quien} retiró la pareja ${q(str("equipo"))} del campeonato ${q(str("campeonato"))}.`;
      break;
    case "campeonato_partido.crear":
      resumen = `${quien} creó un partido${str("fase") ? ` de la fase ${q(str("fase"))}` : ""} en el campeonato ${q(str("campeonato"))}.`;
      break;
    case "campeonato_partido.actualizar":
      resumen = `${quien} modificó un partido del campeonato ${q(str("campeonato"))}${d.partidoId ? ` (partido nº ${String(d.partidoId)})` : ""}.`;
      break;
    case "campeonato_partido.eliminar":
      resumen = `${quien} eliminó un partido del campeonato ${q(str("campeonato"))}${d.partidoId ? ` (partido nº ${String(d.partidoId)})` : ""}.`;
      break;

    default:
      resumen = `${quien} realizó la acción «${describeAccion(r.accion)}».`;
  }

  return { titulo: describeAccion(r.accion), resumen, datos };
}
